import { API_CONFIG, DEFAULT_PARAMS } from '../config/api';
import type {
  ApiRequestParams,
  UserNodesResponse,
  ServiceAccountNodesResponse,
  ResourceNodesResponse,
  RoleNodesResponse,
  QueryResultsResponse,
  CloudType,
} from '../types/api';
import { GraphData, GraphNode, GraphEdge } from '../types/graph';
import {
  fetchUserNodes,
  fetchServiceAccountNodes,
  fetchResourceNodes,
  fetchAlertNodes,
  fetchRoleNodes,
  fetchUserEdges,
  fetchNhiEdges,
  fetchResourceEdges,
  fetchAlertEdges,
  fetchQueryResults,
} from './cloudApi';

// Edge API response types
interface UserEdgesResponse {
  [userEmail: string]: {
    [role: string]: string[]; // resources
  };
}

interface NhiEdgesResponse {
  [saEmail: string]: {
    [role: string]: string[]; // resources
  };
}

interface ResourceEdgesResponse {
  [resourceType: string]: {
    [resourceName: string]: string[]; // users
  };
}

interface AlertEdgesResponse {
  [userEmail: string]: {
    [alertId: string]: string; // resource
  };
}

/**
 * Fetch all graph data from backend APIs
 */
export const fetchGraphData = async (
  params: ApiRequestParams = DEFAULT_PARAMS
): Promise<{
  userNodes: UserNodesResponse;
  serviceAccountNodes: ServiceAccountNodesResponse;
  resourceNodes: ResourceNodesResponse;
  roleNodes: RoleNodesResponse;
  alertNodes: any;
  userEdges: UserEdgesResponse;
  nhiEdges: NhiEdgesResponse;
  resourceEdges: ResourceEdgesResponse;
  alertEdges: AlertEdgesResponse;
  queryResults: QueryResultsResponse;
}> => {
  try {
    const { tenantName, cloudId, cloudType } = params;

    // Call all APIs in parallel
    const [
      userNodes,
      serviceAccountNodes,
      resourceNodes,
      roleNodes,
      alertNodes,
      userEdges,
      nhiEdges,
      resourceEdges,
      alertEdges,
      queryResults,
    ] = await Promise.all([
      fetchUserNodes(tenantName, cloudId, cloudType),
      fetchServiceAccountNodes(tenantName, cloudId, cloudType),
      fetchResourceNodes(tenantName, cloudId, cloudType),
      fetchRoleNodes(tenantName, cloudId, cloudType),
      fetchAlertNodes(tenantName, cloudId, cloudType),
      fetchUserEdges(tenantName, cloudId, cloudType),
      fetchNhiEdges(tenantName, cloudId, cloudType),
      fetchResourceEdges(tenantName, cloudId, cloudType),
      fetchAlertEdges(tenantName, cloudId, cloudType),
      fetchQueryResults(tenantName, cloudId, cloudType),
    ]);

    return {
      userNodes,
      serviceAccountNodes,
      resourceNodes,
      roleNodes,
      alertNodes,
      userEdges,
      nhiEdges,
      resourceEdges,
      alertEdges,
      queryResults,
    };
  } catch (err) {
    console.error('Error fetching graph data from APIs:', err);
    throw err;
  }
};

/**
 * Transform API response to GraphData format
 * Primary source of graph structure: Edge APIs
 * Node APIs provide enrichment data for node details
 */
export const transformApiDataToGraph = (
  apiData: {
    userNodes: UserNodesResponse;
    serviceAccountNodes: ServiceAccountNodesResponse;
    resourceNodes: ResourceNodesResponse;
    roleNodes: RoleNodesResponse;
    alertNodes?: any;
    userEdges?: UserEdgesResponse;
    nhiEdges?: NhiEdgesResponse;
    resourceEdges?: ResourceEdgesResponse;
    alertEdges?: AlertEdgesResponse;
    queryResults: QueryResultsResponse;
  },
  context: { cloudType: CloudType; cloudId: string; tenantName: string },
): GraphData => {
  const { cloudType, cloudId, tenantName } = context;
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const nodeIds = new Set<string>();

  // Node detail maps for enrichment
  const userNodeDetails = apiData?.userNodes || {};
  const saNodeDetails = apiData?.serviceAccountNodes || {};
  const resourceNodeDetails = apiData?.resourceNodes || {};
  const roleNodeDetails = apiData?.roleNodes || {};
  const alertNodeDetails = apiData?.alertNodes || {};

  // Edge data (primary source of graph structure)
  const userEdges = apiData?.userEdges || {};
  const nhiEdges = apiData?.nhiEdges || {};
  let resourceEdges: ResourceEdgesResponse = (apiData?.resourceEdges || {}) as ResourceEdgesResponse;
  const alertEdges = apiData?.alertEdges || {};

  // AWS: resource-edges may be sparse/empty for some resource types (e.g. rds).
  // Still show resources that exist in get-resource-nodes by creating empty edge entries.
  if (cloudType === 'AWS') {
    const ensureAwsResources = (resourceType: string) => {
      const nodeMap = (resourceNodeDetails as any)?.[resourceType] || {};
      if (!resourceEdges[resourceType]) resourceEdges[resourceType] = {} as any;
      Object.keys(nodeMap).forEach((resourceKey) => {
        if (!(resourceKey in (resourceEdges as any)[resourceType])) {
          (resourceEdges as any)[resourceType][resourceKey] = [];
        }
      });
    };

    ['ec2', 's3', 'rds'].forEach(ensureAwsResources);

    // Debug: confirm we have resource keys to render
    console.log('[AWS graph] resource-nodes keys:', {
      ec2: Object.keys(((resourceNodeDetails as any)?.ec2) || {}).length,
      s3: Object.keys(((resourceNodeDetails as any)?.s3) || {}).length,
      rds: Object.keys(((resourceNodeDetails as any)?.rds) || {}).length,
    });
    console.log('[AWS graph] resource-edges keys (after merge):', {
      ec2: Object.keys((resourceEdges as any)?.ec2 || {}).length,
      s3: Object.keys((resourceEdges as any)?.s3 || {}).length,
      rds: Object.keys((resourceEdges as any)?.rds || {}).length,
    });
  }

  let edgeId = 0;

  // Helper to create a unique node ID based on type and name
  const createNodeId = (type: string, name: string): string => {
    switch (type) {
      case 'user': return `user-${name}`;
      case 'service_account': return `sa-${name}`;
      case 'role': return `role-${name}`;
      case 'resource': return `resource-${name}`;
      case 'alert': return `alert-${name}`;
      default: return `${type}-${name}`;
    }
  };

  // Helper to calculate user counts from edges
  const getUserCounts = (userEmail: string) => {
    const roleMap = userEdges[userEmail] || {};
    const roleCount = Object.keys(roleMap).length;

    // Deduplicate resources across all roles
    const uniqueResources = new Set<string>();
    Object.values(roleMap).forEach((resources) => {
      (resources || []).forEach((res) => uniqueResources.add(res));
    });
    const resourceCount = uniqueResources.size;

    // Count alerts for this user and find most recent alert date
    const userAlerts = alertEdges[userEmail] || {};
    const alertCount = Object.keys(userAlerts).length;
    let criticalAlertCount = 0;

    // Find the most recent alert createdAt for this user
    let mostRecentAlertDate: string | null = null;
    Object.keys(userAlerts).forEach((alertId) => {
      const alert = alertNodeDetails[alertId];
      if (alert?.severity?.toLowerCase() === 'critical') {
        criticalAlertCount++;
      }
      if (alert?.createdAt) {
        if (!mostRecentAlertDate || new Date(alert.createdAt) > new Date(mostRecentAlertDate)) {
          mostRecentAlertDate = alert.createdAt;
        }
      }
    });

    return { roleCount, resourceCount, alertCount, criticalAlertCount, mostRecentAlertDate };
  };

  // Helper to get user node details (GCP, Azure, and AWS)
  const getUserDetails = (email: string) => {
    const user = userNodeDetails[email];
    if (!user) return null;

    // Determine cloud type
    const isAzure = cloudType === 'AZURE';
    const isAws = cloudType === 'AWS';

    // Build role names map from roleNodeDetails
    // For Azure, roles come from role-nodes API as arrays of role names per user azureId
    // OR from userEdges keys if not found in roleNodeDetails
    // For AWS, roles come from userPolicies in user node or roleNodeDetails (policies by user ARN)
    let roles: string[] = (user as any).roles || [];
    const roleNames: Record<string, string> = {};

    // For Azure, check if roleNodeDetails has this user's roles as array
    // Try both email and azureId as keys
    const azureId = (user as any).id || (user as any).azureId;
    const azureRoles = roleNodeDetails[email] || (azureId ? roleNodeDetails[azureId] : null);

    if (isAws) {
      // AWS format: user has userPolicies array with policy objects
      // roleNodeDetails[userArn] = [{ policyARN, policyName, ... }, ...]
      const awsPolicies = roleNodeDetails[email] || (user as any).userPolicies || [];
      if (Array.isArray(awsPolicies)) {
        awsPolicies.forEach((policy: any) => {
          const policyArn = policy.policyARN || policy;
          const policyName = policy.policyName || policyArn.split('/').pop() || policyArn;
          roles.push(policyArn);
          roleNames[policyArn] = policyName;
        });
      }
      // Also get from userEdges if available
      const userRoleMap = userEdges[email] || {};
      Object.keys(userRoleMap).forEach((policyArn: string) => {
        if (!roles.includes(policyArn)) {
          roles.push(policyArn);
          roleNames[policyArn] = policyArn.split('/').pop() || policyArn;
        }
      });
    } else if (isAzure && Array.isArray(azureRoles)) {
      // Azure format: roleNodeDetails[azureId] = ["Role1", "Role2", ...]
      azureRoles.forEach((roleName: string) => {
        roleNames[roleName] = roleName;
      });
      roles = azureRoles;
    } else if (isAzure) {
      // Azure fallback: get roles from userEdges if roleNodeDetails is empty
      const userRoleMap = userEdges[email] || {};
      const edgeRoles = Object.keys(userRoleMap);
      if (edgeRoles.length > 0) {
        roles = edgeRoles;
        edgeRoles.forEach((roleName: string) => {
          roleNames[roleName] = roleName;
        });
      }
    } else {
      // GCP format: roleNodeDetails[roleId] = { roleName, ... }
      roles.forEach((roleId: string) => {
        const roleDetail = roleNodeDetails[roleId];
        if (roleDetail) {
          roleNames[roleId] = roleDetail.roleName || roleId.split('/').pop() || roleId;
        } else {
          const roleSuffix = roleId.split('/').pop() || roleId;
          roleNames[roleId] = roleSuffix.charAt(0).toUpperCase() + roleSuffix.slice(1).replace(/([A-Z])/g, ' $1').trim();
        }
      });
    }

    return {
      // Common fields
      email: (user as any).email || (user as any).userPrincipalName || email,
      userName: (user as any).userName || (user as any).displayName || (user as any).username,
      roles,
      roleNames,
      syncedAt: (user as any).syncedAt,
      projectId: (user as any).projectId,
      tenantName: (user as any).tenantName || (user as any).wsTenantName,
      lastActivity: (user as any).lastActivity,
      isDormant: (user as any).isDormant,
      activity: (user as any).activity || 0,
      lastActivityMessage: (user as any).lastActivityMessage || '',
      // Azure-specific fields
      ...(isAzure && {
        azureId: (user as any).azureId ?? (user as any).id,
        // Alias used by Azure analytics APIs
        userId: (user as any).azureId ?? (user as any).id,
        displayName: (user as any).displayName,
        userPrincipalName: (user as any).userPrincipalName,
        givenName: (user as any).givenName,
        surname: (user as any).surname,
        accountEnabled: (user as any).accountEnabled,
        mail: (user as any).mail,
        mobilePhone: (user as any).mobilePhone,
        jobTitle: (user as any).jobTitle,
        department: (user as any).department,
        officeLocation: (user as any).officeLocation,
        createdDateTime: (user as any).createdDateTime,
        preferredLanguage: (user as any).preferredLanguage,
        isSSOEnabled: (user as any).isSSOEnabled,
        cloudType: 'AZURE',
        subscriptionId: (user as any).subscriptionId ?? (user as any).azureSubscriptionId ?? cloudId,
        azureSubscriptionId: (user as any).azureSubscriptionId ?? (user as any).subscriptionId ?? cloudId,
      }),
      // AWS-specific fields
      ...(isAws && {
        arn: (user as any).arn || email,
        username: (user as any).username,
        userId: (user as any).userId,
        createDate: (user as any).createDate,
        path: (user as any).path,
        accountId: (user as any).accountId,
        groups: (user as any).groups,
        cloudType: 'AWS',
      }),
    };
  };

  // Helper to get service account node details (GCP, Azure Managed Identity, and AWS IAM Role)
  const getSaDetails = (email: string) => {
    const sa = saNodeDetails[email];
    if (!sa) return null;

    // Determine cloud type
    const isAzure = cloudType === 'AZURE';
    const isAws = cloudType === 'AWS';

    // Build role names map from roleNodeDetails
    let roles: string[] = (sa as any).roles || [];
    const roleNames: Record<string, string> = {};

    // For Azure, check if roleNodeDetails has this identity's roles as array
    // Try both email and principalId as keys
    const principalId = (sa as any).principalId;
    const azureRoles = roleNodeDetails[email] || (principalId ? roleNodeDetails[principalId] : null);

    if (isAws) {
      // AWS IAM Roles: inlinePolicyNames are the attached policies
      const inlinePolicies = (sa as any).inlinePolicyNames || [];
      inlinePolicies.forEach((policyName: string) => {
        roles.push(policyName);
        roleNames[policyName] = policyName;
      });
      // Also get from nhiEdges if available
      const saRoleMap = nhiEdges[email] || {};
      Object.keys(saRoleMap).forEach((policyName: string) => {
        if (!roles.includes(policyName)) {
          roles.push(policyName);
          roleNames[policyName] = policyName;
        }
      });
    } else if (isAzure && Array.isArray(azureRoles)) {
      azureRoles.forEach((roleName: string) => {
        roleNames[roleName] = roleName;
      });
      roles = azureRoles;
    } else if (isAzure) {
      // Azure fallback: get roles from nhiEdges if roleNodeDetails is empty
      const saRoleMap = nhiEdges[email] || {};
      const edgeRoles = Object.keys(saRoleMap);
      if (edgeRoles.length > 0) {
        roles = edgeRoles;
        edgeRoles.forEach((roleName: string) => {
          roleNames[roleName] = roleName;
        });
      }
    } else {
      // GCP format
      roles.forEach((roleId: string) => {
        const roleDetail = roleNodeDetails[roleId];
        if (roleDetail) {
          roleNames[roleId] = roleDetail.roleName || roleId.split('/').pop() || roleId;
        } else {
          const roleSuffix = roleId.split('/').pop() || roleId;
          roleNames[roleId] = roleSuffix.charAt(0).toUpperCase() + roleSuffix.slice(1).replace(/([A-Z])/g, ' $1').trim();
        }
      });
    }

    return {
      // Common fields - for Azure use name, for AWS use roleName
      email: (sa as any).serviceAccountEmail || (sa as any).name || (sa as any).roleArn || email,
      serviceAccountEmail: (sa as any).serviceAccountEmail || (sa as any).name || (sa as any).roleArn || email,
      roles,
      roleNames,
      syncedAt: (sa as any).syncedAt,
      projectId: (sa as any).projectId,
      isUserManaged: (sa as any).isUserManaged,
      tenantName: (sa as any).tenantName || (sa as any).wsTenantName,
      lastActivity: (sa as any).lastActivity,
      isDormant: (sa as any).isDormant,
      activity: (sa as any).activity || 0,
      lastActivityMessage: (sa as any).lastActivityMessage || '',
      // Azure-specific fields for Managed Identity
      ...(isAzure && {
        instanceId: (sa as any).instanceId,
        name: (sa as any).name,
        resourceGroup: (sa as any).resourceGroup,
        region: (sa as any).region,
        identityType: (sa as any).type,
        principalId: (sa as any).principalId,
        subscriptionId: (sa as any).subscriptionId,
        cloudType: 'AZURE',
      }),
      // AWS-specific fields for IAM Role
      ...(isAws && {
        roleArn: (sa as any).roleArn || email,
        roleName: (sa as any).roleName,
        accountId: (sa as any).accountId,
        path: (sa as any).path,
        regions: (sa as any).regions || [],
        description: (sa as any).description,
        createDate: (sa as any).createDate,
        lastUsedAt: (sa as any).lastUsedAt,
        maxSessionDuration: (sa as any).maxSessionDuration,
        inlinePolicyNames: (sa as any).inlinePolicyNames || [],
        cloudType: 'AWS',
      }),
    };
  };

  // Helper to calculate SA counts from edges (similar to getUserCounts)
  const getSaCounts = (saEmail: string) => {
    const roleMap = nhiEdges[saEmail] || {};
    const roleCount = Object.keys(roleMap).length;

    // Deduplicate resources across all roles (filter out SERVICE_ACCOUNT placeholder)
    const uniqueResources = new Set<string>();
    Object.values(roleMap).forEach((resources) => {
      (resources || []).filter((r) => r !== 'SERVICE_ACCOUNT').forEach((res) => uniqueResources.add(res));
    });
    const resourceCount = uniqueResources.size;

    // SA alerts would need to be tracked if available - for now set to 0
    // Check if SA email appears in alertEdges
    const saAlerts = alertEdges[saEmail] || {};
    const alertCount = Object.keys(saAlerts).length;
    let criticalAlertCount = 0;

    // Find the most recent alert createdAt for this SA
    let mostRecentAlertDate: string | null = null;
    Object.keys(saAlerts).forEach((alertId) => {
      const alert = alertNodeDetails[alertId];
      if (alert?.severity?.toLowerCase() === 'critical') {
        criticalAlertCount++;
      }
      if (alert?.createdAt) {
        if (!mostRecentAlertDate || new Date(alert.createdAt) > new Date(mostRecentAlertDate)) {
          mostRecentAlertDate = alert.createdAt;
        }
      }
    });

    return { roleCount, resourceCount, alertCount, criticalAlertCount, mostRecentAlertDate };
  };

  // Helper to get role node details
  const getRoleDetails = (roleId: string) => {
    const role = roleNodeDetails[roleId];
    if (!role) return null;
    return {
      roleId,
      roleName: role.roleName,
      description: role.description,
      isCustom: role.isCustom,
      isManagedRole: role.isManagedRole,
    };
  };

  // Helper to find resource details by name across all resource types (GCP, Azure, and AWS)
  const getResourceDetails = (resourceName: string) => {
    const isAzure = cloudType === 'AZURE';
    const isAws = cloudType === 'AWS';

    // AWS has nested resource structure: { ec2: {...}, rds: {...}, s3: {...} }
    if (isAws) {
      const awsResources = resourceNodeDetails as any;

      // Check EC2 instances
      const ec2Map = awsResources?.ec2 || {};
      const ec2 = ec2Map[resourceName] || Object.values(ec2Map).find((e: any) =>
        e.instanceName === resourceName || e.instanceId === resourceName
      );
      if (ec2) {
        return {
          resourceType: 'EC2',
          resourceCategory: 'ec2',
          name: (ec2 as any).instanceName || resourceName,
          instanceId: (ec2 as any).instanceId,
          instanceArn: (ec2 as any).instanceArn,
          instanceState: (ec2 as any).instanceState,
          instanceType: (ec2 as any).instanceType,
          architecture: (ec2 as any).architecture,
          platformDetails: (ec2 as any).platformDetails,
          vpcId: (ec2 as any).vpcId,
          subnetId: (ec2 as any).subnetId,
          privateDnsName: (ec2 as any).privateDnsName,
          privateIpAddress: (ec2 as any).privateIpAddress,
          publicDnsName: (ec2 as any).publicDnsName,
          publicIpAddress: (ec2 as any).publicIpAddress,
          availabilityZone: (ec2 as any).availabilityZone,
          securityGroups: (ec2 as any).securityGroups,
          keyName: (ec2 as any).keyName,
          launchTime: (ec2 as any).launchTime,
          isManageInstance: (ec2 as any).isManageInstance,
          tenantName: (ec2 as any).tenantName || (ec2 as any).awsAccount?.tenantName,
          accountId: (ec2 as any).awsAccount?.accountId,
          cloudProvider: 'AWS',
          activity: (ec2 as any).activity || 0,
          lastActivity: (ec2 as any).lastActivity,
          lastActivityMessage: (ec2 as any).lastActivityMessage || '',
          cloudType: 'AWS',
        };
      }

      // Check RDS instances
      const rdsMap = awsResources?.rds || {};
      const rds = rdsMap[resourceName] || Object.values(rdsMap).find((r: any) =>
        r.rdsIdentifierName === resourceName || r.rdsDBInstanceArn === resourceName
      );
      if (rds) {
        return {
          resourceType: 'RDS',
          resourceCategory: 'rds',
          name: (rds as any).rdsIdentifierName || resourceName,
          rdsDBInstanceArn: (rds as any).rdsDBInstanceArn,
          rdsEngine: (rds as any).rdsEngine,
          rdsAvailabilityZone: (rds as any).rdsAvailabilityZone,
          rdsInstanceStatus: (rds as any).rdsInstanceStatus,
          rdsInstanceMasterUserName: (rds as any).rdsInstanceMasterUserName,
          rdsInstanceCreatedTime: (rds as any).rdsInstanceCreatedTime,
          rdsLicenseModel: (rds as any).rdsLicenseModel,
          rdsKmsKeyId: (rds as any).rdsKmsKeyId,
          rdsDbiResourceId: (rds as any).rdsDbiResourceId,
          rdsMonitoringInterval: (rds as any).rdsMonitoringInterval,
          rdsMaxAllocatedStorage: (rds as any).rdsMaxAllocatedStorage,
          rdsNetworkType: (rds as any).rdsNetworkType,
          isManagedRDS: (rds as any).isManagedRDS,
          tenantName: (rds as any).tenantName || (rds as any).awsAccount?.tenantName,
          accountId: (rds as any).awsAccount?.accountId,
          cloudProvider: 'AWS',
          activity: (rds as any).activity || 0,
          lastActivity: (rds as any).lastActivity,
          lastActivityMessage: (rds as any).lastActivityMessage || '',
          cloudType: 'AWS',
        };
      }

      // Check S3 buckets
      const s3Map = awsResources?.s3 || {};
      const s3 = s3Map[resourceName] || Object.values(s3Map).find((s: any) =>
        s.bucketName === resourceName
      );
      if (s3) {
        return {
          resourceType: 'S3',
          resourceCategory: 's3',
          name: (s3 as any).bucketName || resourceName,
          bucketName: (s3 as any).bucketName,
          creationDate: (s3 as any).creationDate,
          region: (s3 as any).region,
          isManagedS3: (s3 as any).isManagedS3,
          tenantName: (s3 as any).tenantName || (s3 as any).awsAccount?.tenantName,
          accountId: (s3 as any).awsAccount?.accountId,
          cloudProvider: 'AWS',
          activity: (s3 as any).activity || 0,
          lastActivity: (s3 as any).lastActivity,
          lastActivityMessage: (s3 as any).lastActivityMessage || '',
          cloudType: 'AWS',
        };
      }

      // Default for AWS
      return {
        resourceType: 'Unknown',
        resourceCategory: 'unknown',
        name: resourceName,
        cloudProvider: 'AWS',
        cloudType: 'AWS',
      };
    }

    // Check virtual machines (GCP and Azure)
    const vmMap = resourceNodeDetails['virtual-machine'] || {};
    // For Azure, keys might be UUIDs, so search by name field
    let vm: any = vmMap[resourceName];
    if (!vm && isAzure) {
      vm = Object.values(vmMap).find((v: any) => v.name === resourceName);
    }
    if (vm) {
      return {
        resourceType: 'VM',
        resourceCategory: 'virtual-machine',
        name: (vm as any).name || resourceName,
        zone: (vm as any).zone,
        status: (vm as any).status || (vm as any).powerState,
        machineType: (vm as any).machineType || (vm as any).size,
        ip: (vm as any).ip || (vm as any).ipAddress,
        projectId: (vm as any).projectId,
        tenantName: (vm as any).tenantName || (vm as any).wsTenantName,
        createdAt: (vm as any).createdAt,
        stackType: (vm as any).stackType,
        description: (vm as any).description,
        isManagedVirtualMachine: (vm as any).isManagedVirtualMachine,
        cloudProvider: cloudType,
        activity: (vm as any).activity || 0,
        lastActivity: (vm as any).lastActivity,
        lastActivityMessage: (vm as any).lastActivityMessage || '',
        // Azure-specific VM fields
        ...(isAzure && {
          computerName: (vm as any).computerName,
          powerState: (vm as any).powerState,
          size: (vm as any).size,
          osType: (vm as any).osType,
          region: (vm as any).region,
          securityType: (vm as any).securityType,
          resourceGroupName: (vm as any).resourceGroupName,
          osDiskSize: (vm as any).osDiskSize,
          subscriptionId: (vm as any).subscriptionId,
        }),
      };
    }

    // Check databases
    const dbMap = resourceNodeDetails['database'] || {};
    let db: any = dbMap[resourceName];
    if (!db && isAzure) {
      db = Object.values(dbMap).find((d: any) => d.databaseName === resourceName);
    }
    if (db) {
      return {
        resourceType: 'Database',
        resourceCategory: 'database',
        name: (db as any).name || (db as any).databaseName || resourceName,
        instanceType: (db as any).instanceType || (db as any).databaseType,
        state: (db as any).state || (db as any).status || (db as any).dbStatus,
        zone: (db as any).zone,
        region: (db as any).region,
        ipAddress: (db as any).ipAddress,
        dataDiskSizeGb: (db as any).dataDiskSizeGb || (db as any).sizeInGb,
        dataDiskType: (db as any).dataDiskType,
        tier: (db as any).tier,
        edition: (db as any).edition,
        databaseVersion: (db as any).databaseVersion || (db as any).version,
        projectId: (db as any).projectId,
        tenantName: (db as any).tenantName || (db as any).wsTenantName,
        isManagedDatabase: (db as any).isManagedDatabase,
        cloudProvider: cloudType,
        activity: (db as any).activity || 0,
        lastActivity: (db as any).lastActivity,
        lastActivityMessage: (db as any).lastActivityMessage || '',
        // Azure-specific DB fields
        ...(isAzure && {
          databaseName: (db as any).databaseName,
          databaseType: (db as any).databaseType,
          azureServerId: (db as any).azureServerId,
          lastBackupTime: (db as any).lastBackupTime,
          createdDate: (db as any).createdDate,
          maxSizeBytes: (db as any).maxSizeBytes,
          dbStatus: (db as any).dbStatus,
          readScale: (db as any).readScale,
          minCapacity: (db as any).minCapacity,
          defaultSecondaryLocation: (db as any).defaultSecondaryLocation,
          resourceGroupName: (db as any).resourceGroupName,
          subscriptionId: (db as any).subscriptionId,
        }),
      };
    }

    // Check storage buckets/accounts
    const bucketMap = resourceNodeDetails['storage-bucket'] || {};
    let bucket: any = bucketMap[resourceName];
    if (!bucket && isAzure) {
      bucket = Object.values(bucketMap).find((b: any) => b.storageAccountName === resourceName);
    }
    if (bucket) {
      return {
        resourceType: 'Storage Bucket',
        resourceCategory: 'storage-bucket',
        name: (bucket as any).name || (bucket as any).storageAccountName || resourceName,
        location: (bucket as any).location || (bucket as any).region,
        storageClass: (bucket as any).storageClass || (bucket as any).accessTier,
        createdTime: (bucket as any).createdTime || (bucket as any).createdDate,
        projectId: (bucket as any).projectId,
        tenantName: (bucket as any).tenantName || (bucket as any).wsTenantName,
        isManagedStorageBucket: (bucket as any).isManagedStorageBucket,
        cloudProvider: cloudType,
        activity: (bucket as any).activity || 0,
        lastActivity: (bucket as any).lastActivity,
        lastActivityMessage: (bucket as any).lastActivityMessage || '',
        // Azure-specific Storage fields
        ...(isAzure && {
          storageAccountName: (bucket as any).storageAccountName,
          kind: (bucket as any).kind,
          skuTier: (bucket as any).skuTier,
          accessTier: (bucket as any).accessTier,
          publicAccess: (bucket as any).publicAccess,
          blobPublicAccessAllowed: (bucket as any).blobPublicAccessAllowed,
          sharedKeyAccessAllowed: (bucket as any).sharedKeyAccessAllowed,
          isAccessAllowedFromAllNetworks: (bucket as any).isAccessAllowedFromAllNetworks,
          resourceGroupName: (bucket as any).resourceGroupName,
          subscriptionId: (bucket as any).subscriptionId,
        }),
      };
    }

    // Check secrets
    const secret = resourceNodeDetails['secrets']?.[resourceName];
    if (secret) {
      return {
        resourceType: 'Secret',
        resourceCategory: 'secrets',
        name: (secret as any).name || resourceName,
        displayName: (secret as any).displayName,
        projectId: (secret as any).projectId,
        tenantName: (secret as any).tenantName,
        cloudProvider: cloudType,
      };
    }

    // Default - resource without details
    return {
      resourceType: 'Unknown',
      resourceCategory: 'unknown',
      name: resourceName,
      cloudProvider: cloudType,
    };
  };

  // Helper to get alerts affecting a resource with most recent date
  const getResourceAlerts = (resourceName: string): { alertIds: string[]; criticalAlertCount: number; mostRecentAlertDate: string | null } => {
    const alertIds: string[] = [];
    let criticalAlertCount = 0;
    let mostRecentAlertDate: string | null = null;

    Object.entries(alertNodeDetails).forEach(([alertId, alert]: [string, any]) => {
      if (alert.affectedResources?.includes(resourceName)) {
        alertIds.push(alertId);
        if (alert.severity?.toLowerCase() === 'critical') {
          criticalAlertCount++;
        }
        if (alert.createdAt) {
          if (!mostRecentAlertDate || new Date(alert.createdAt) > new Date(mostRecentAlertDate)) {
            mostRecentAlertDate = alert.createdAt;
          }
        }
      }
    });
    return { alertIds, criticalAlertCount, mostRecentAlertDate };
  };

  // Helper to get alert node details from get-alert-nodes API
  const getAlertDetails = (alertId: string) => {
    const alert = alertNodeDetails[alertId];
    if (!alert) return null;
    return {
      alertId: alert.alertId || alertId,
      title: alert.title || 'Unknown Alert',
      severity: alert.severity || 'medium',
      alertType: alert.alertType || 'RULE',
      cloudType: alert.cloudType || cloudType,
      entity: alert.entity,
      affectedResources: alert.affectedResources || [],
      attackPath: alert.attackPath || {},
      info: alert.info || {},
      mitre: alert.mitre || {},
      timeRangeStart: alert.timeRangeStart,
      timeRangeEnd: alert.timeRangeEnd,
      createdAt: alert.createdAt,
      isNonHumanEntity: alert.isNonHumanEntity,
      isFlagged: alert.isFlagged,
    };
  };

  // Helper to add node if not exists
  const addNode = (id: string, type: string, label: string, graphId: string, props?: any) => {
    if (!nodeIds.has(id)) {
      nodes.push({
        id,
        type: type as any,
        label,
        provider: cloudType.toLowerCase() as any,
        props: {
          ...props,
          graphId,
          cloudProvider: cloudType.toUpperCase(),
          cloudType: cloudType.toUpperCase(),
          cloudId,
          tenantName: (props as any)?.tenantName ?? tenantName,
        },
      });
      nodeIds.add(id);
    }
  };

  // Helper to add edge
  const addEdge = (source: string, target: string, type: string, graphId: string) => {
    edges.push({
      id: `edge-${edgeId++}`,
      source,
      target,
      type: type as any,
      props: { graphId },
    });
  };

  // =====================================================
  // Helper: Merge alerts that share the same title under a parent node.
  // When multiple alerts have the same title, only ONE anomaly node is created
  // (for user / SA / IAM / resource parents). The merged node carries:
  //   - affectedResources: union across every merged alert
  //   - the LATEST alert's details/timeline (createdAt, timeRange, attackPath, etc.)
  //   - mergedAlertIds / mergedAlertCount metadata
  // Returns one group per unique title so callers can attach child hierarchy.
  // =====================================================
  interface MergedAlertGroup {
    title: string;
    alertNodeId: string;
    alertIds: string[]; // all alert IDs merged into this node
    representativeAlertId: string;
    affectedResources: string[]; // union across all merged alerts
  }

  const addMergedAlertNodes = (
    parentNodeId: string,
    graphId: string,
    alertEntries: Array<{ alertId: string; resourceName?: string }>,
    parentProp: Record<string, any>
  ): MergedAlertGroup[] => {
    // Group alert entries by their resolved title
    const groups = new Map<string, { alertIds: string[]; resourceNames: Set<string> }>();
    alertEntries.forEach(({ alertId, resourceName }) => {
      const details = getAlertDetails(alertId);
      const title = details?.title || alertId;
      if (!groups.has(title)) {
        groups.set(title, { alertIds: [], resourceNames: new Set<string>() });
      }
      const g = groups.get(title)!;
      if (!g.alertIds.includes(alertId)) g.alertIds.push(alertId);
      if (resourceName) g.resourceNames.add(resourceName);
    });

    const result: MergedAlertGroup[] = [];

    groups.forEach((g, title) => {
      // Resolve full details for each merged alert
      const detailsList = g.alertIds
        .map((id) => getAlertDetails(id))
        .filter((d): d is NonNullable<ReturnType<typeof getAlertDetails>> => Boolean(d));

      // Pick the most recent alert as the representative (latest createdAt)
      const sortedByRecent = [...detailsList].sort((a, b) => {
        const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return db - da;
      });
      const latest = sortedByRecent[0];
      const representativeAlertId = latest?.alertId || g.alertIds[0];

      // Union of affected resources across every merged alert (+ edge-mapped resources)
      const mergedResources = new Set<string>();
      detailsList.forEach((d) => (d.affectedResources || []).forEach((r: string) => mergedResources.add(r)));
      g.resourceNames.forEach((r) => mergedResources.add(r));
      const affectedResources = Array.from(mergedResources);

      const alertNodeId = createNodeId('alert', `${graphId}-${representativeAlertId}`);

      addNode(alertNodeId, 'anomaly', title, graphId, {
        ...(latest || {}),
        alertId: representativeAlertId,
        affectedResources,
        // Merge metadata
        mergedAlertIds: g.alertIds,
        mergedAlertCount: g.alertIds.length,
        isMergedAlert: g.alertIds.length > 1,
        ...parentProp,
      });

      addEdge(parentNodeId, alertNodeId, 'emits_anomaly', graphId);

      result.push({
        title,
        alertNodeId,
        alertIds: g.alertIds,
        representativeAlertId,
        affectedResources,
      });
    });

    return result;
  };

  // =====================================================
  // Process User Edges: User → Role → Resource hierarchy
  // ONLY include users that exist in userNodeDetails (intersection)
  // =====================================================
  Object.entries(userEdges).forEach(([userEmail, roleMap]) => {
    // CRITICAL: Only process users that exist in user-nodes API
    if (!userNodeDetails[userEmail]) {
      console.log(`Skipping user ${userEmail} - not in user-nodes API`);
      return;
    }

    const graphId = `user-${userEmail}`;
    const userId = createNodeId('user', userEmail);
    const userDetails = getUserDetails(userEmail);
    const counts = getUserCounts(userEmail);
    const userName = userDetails?.userName || userEmail.split('@')[0];

    // Add user node with counts and activity data
    addNode(userId, 'user', userName, graphId, {
      ...userDetails,
      roleCount: counts.roleCount,
      resourceCount: counts.resourceCount,
      alertCount: counts.alertCount,
      criticalAlertCount: counts.criticalAlertCount,
      mostRecentAlertDate: counts.mostRecentAlertDate,
    });

    // Process roles and resources for this user
    Object.entries(roleMap || {}).forEach(([roleName, resources]) => {
      const roleId = createNodeId('role', `${graphId}-${roleName}`);
      const roleDetails = getRoleDetails(roleName);
      const roleLabel = roleDetails?.roleName || roleName.split('/').pop() || roleName;

      // Add role node (scoped to this user's graph)
      addNode(roleId, 'role', roleLabel, graphId, { ...roleDetails, parentUser: userEmail });

      // Add edge: User → Role
      addEdge(userId, roleId, 'has_role', graphId);

      // Process resources connected through this role
      // CRITICAL: Scope resource ID to BOTH graph AND role so same resource under multiple roles
      // creates separate nodes that expand in their parent role's direction
      (resources || []).forEach((resourceName) => {
        // Use encoded, collision-free scoping so the same resource can appear under multiple roles
        // without collapsing into one node (which would connect to multiple roles).
        const resourceScope = `${graphId}::${encodeURIComponent(roleName)}::${encodeURIComponent(resourceName)}`;
        const resourceId = createNodeId('resource', resourceScope);
        const resourceDetails = getResourceDetails(resourceName);

        // Add resource node (scoped to this user's graph)
        addNode(resourceId, 'resource', resourceName, graphId, { ...resourceDetails, parentRole: roleName });

        // Add edge: Role → Resource
        addEdge(roleId, resourceId, 'accesses', graphId);
      });
    });
  });

  // =====================================================
  // Process NHI (Service Account) Edges: SA → Role → Resource hierarchy
  // ONLY include SAs that exist in saNodeDetails (intersection)
  // =====================================================
  Object.entries(nhiEdges).forEach(([saEmail, roleMap]) => {
    // CRITICAL: Only process SAs that exist in sa-nodes API
    if (!saNodeDetails[saEmail]) {
      console.log(`Skipping SA ${saEmail} - not in sa-nodes API`);
      return;
    }

    const graphId = `sa-${saEmail}`;
    const saId = createNodeId('service_account', saEmail);
    const saDetails = getSaDetails(saEmail);
    const saCounts = getSaCounts(saEmail);
    const saName = saEmail.split('@')[0];

    // Add service account node with counts
    addNode(saId, 'service_account', saName, graphId, {
      ...saDetails,
      roleCount: saCounts.roleCount,
      resourceCount: saCounts.resourceCount,
      alertCount: saCounts.alertCount,
      criticalAlertCount: saCounts.criticalAlertCount,
      mostRecentAlertDate: saCounts.mostRecentAlertDate,
    });

    // AUTO-ATTACH ALERTS to SA nodes (merge alerts that share the same title)
    const saAlertMap = alertEdges[saEmail] || {};
    const saAlertGroups = addMergedAlertNodes(
      saId,
      graphId,
      Object.entries(saAlertMap).map(([alertId, resourceName]) => ({ alertId, resourceName })),
      { parentServiceAccount: saEmail }
    );

    // Attach affected resources (alert hierarchy - NOT visible initially) for each merged alert
    saAlertGroups.forEach((group) => {
      group.affectedResources.forEach((resourceName) => {
        const alertResourceId = createNodeId('resource', `${graphId}-alert-${group.representativeAlertId}-${resourceName}`);
        const resourceDetails = getResourceDetails(resourceName);

        addNode(alertResourceId, 'resource', resourceName, graphId, {
          ...resourceDetails,
          parentAlert: group.representativeAlertId,
          isAlertHierarchy: true,
        });

        // Add edge: Alert → Resource
        addEdge(group.alertNodeId, alertResourceId, 'affects', graphId);
      });
    });

    // Process roles and resources for this service account
    Object.entries(roleMap || {}).forEach(([roleName, resources]) => {
      const roleId = createNodeId('role', `${graphId}-${roleName}`);
      const roleDetails = getRoleDetails(roleName);
      const roleLabel = roleDetails?.roleName || roleName.split('/').pop() || roleName;

      // Add role node (scoped to this SA's graph)
      addNode(roleId, 'role', roleLabel, graphId, { ...roleDetails, parentSa: saEmail });

      // Add edge: SA → Role
      addEdge(saId, roleId, 'has_role', graphId);

      // Process resources connected through this role
      // CRITICAL: Scope resource ID to BOTH graph AND role so same resource under multiple roles
      // creates separate nodes that expand in their parent role's direction
      // FILTER: Ignore "SERVICE_ACCOUNT" placeholder values
      (resources || []).filter((resourceName) => resourceName !== 'SERVICE_ACCOUNT').forEach((resourceName) => {
        // Use encoded, collision-free scoping so the same resource can appear under multiple roles
        // without collapsing into one node (which would connect to multiple roles).
        const resourceScope = `${graphId}::${encodeURIComponent(roleName)}::${encodeURIComponent(resourceName)}`;
        const resourceId = createNodeId('resource', resourceScope);
        const resourceDetails = getResourceDetails(resourceName);

        // Add resource node (scoped to this SA's graph)
        addNode(resourceId, 'resource', resourceName, graphId, { ...resourceDetails, parentRole: roleName });

        // Add edge: Role → Resource
        addEdge(roleId, resourceId, 'accesses', graphId);
      });
    });
  });

  // =====================================================
  // Process Resource Edges: Resource → User hierarchy
  // ResourceType (database, vm, etc.) is metadata, not a node
  // For AWS, resource types are: ec2, rds, s3
  // =====================================================
  Object.entries(resourceEdges).forEach(([resourceType, resourceMap]) => {
    Object.entries(resourceMap || {}).forEach(([resourceName, users]) => {
      const graphId = `resource-center-${resourceName}`;
      const resourceId = createNodeId('resource', `center-${resourceName}`);
      const resourceDetails = getResourceDetails(resourceName);
      const resourceAlerts = getResourceAlerts(resourceName);

      // For AWS, users array might be empty but we still want to show the resource
      const usersArray = Array.isArray(users) ? users : [];
      const userCount = usersArray.filter(u => userNodeDetails[u]).length;

      // Get a display name for the resource
      // For AWS, resourceName might be an ARN - extract the resource identifier
      let displayName = resourceName;
      if (cloudType === 'AWS' && resourceName.includes('/')) {
        displayName = resourceName.split('/').pop() || resourceName;
      }
      if (resourceDetails?.name) {
        displayName = resourceDetails.name;
      }

      // Add resource node as the center of this graph
      addNode(resourceId, 'resource', displayName, graphId, {
        ...resourceDetails,
        resourceCategory: resourceType, // Store resource type as metadata
        isGraphRoot: true,
        alertCount: resourceAlerts.alertIds.length,
        criticalAlertCount: resourceAlerts.criticalAlertCount,
        alertIds: resourceAlerts.alertIds,
        mostRecentAlertDate: resourceAlerts.mostRecentAlertDate,
        userCount,
      });

      // AUTO-ATTACH ALERTS to resource nodes (merge alerts that share the same title)
      const resourceAlertGroups = addMergedAlertNodes(
        resourceId,
        graphId,
        resourceAlerts.alertIds.map((alertId) => ({ alertId })),
        { parentResource: resourceName }
      );

      // For alert hierarchy exploration, attach the users that triggered any of the
      // merged alerts (deduplicated across the merged group).
      resourceAlertGroups.forEach((group) => {
        const seenUsers = new Set<string>();
        group.alertIds.forEach((alertId) => {
          Object.entries(alertEdges).forEach(([userEmail, userAlertMap]) => {
            if (userAlertMap[alertId] && userNodeDetails[userEmail] && !seenUsers.has(userEmail)) {
              seenUsers.add(userEmail);
              const alertUserId = createNodeId('user', `${graphId}-alert-${group.representativeAlertId}-${userEmail}`);
              const userDetails = getUserDetails(userEmail);
              const userName = userDetails?.userName || userEmail.split('@')[0];

              addNode(alertUserId, 'user', userName, graphId, {
                ...userDetails,
                parentAlert: group.representativeAlertId,
                isAlertHierarchy: true,
              });

              // Add edge: Alert → User
              addEdge(group.alertNodeId, alertUserId, 'triggered_by', graphId);
            }
          });
        });
      });

      // Process users connected to this resource (only if user exists in userNodeDetails)
      usersArray.forEach((userEmail) => {
        // Only include users that exist in user-nodes API
        if (!userNodeDetails[userEmail]) {
          return;
        }

        const userId = createNodeId('user', `${graphId}-${userEmail}`);
        const userDetails = getUserDetails(userEmail);
        const userName = userDetails?.userName || userEmail.split('@')[0];

        // Add user node (scoped to this resource's graph)
        addNode(userId, 'user', userName, graphId, { ...userDetails, parentResource: resourceName });

        // Add edge: Resource → User
        addEdge(resourceId, userId, 'accessed_by', graphId);

        // For infinite hierarchy support, get user's roles from userEdges
        const userRoleMap = userEdges[userEmail];
        if (userRoleMap) {
          Object.entries(userRoleMap).forEach(([roleName, _resources]) => {
            const roleId = createNodeId('role', `${graphId}-${userEmail}-${roleName}`);
            const roleDetails = getRoleDetails(roleName);
            const roleLabel = roleDetails?.roleName || roleName.split('/').pop() || roleName;

            // Add role node (scoped to this graph)
            addNode(roleId, 'role', roleLabel, graphId, { ...roleDetails, parentUser: userEmail });

            // Add edge: User → Role (for infinite expansion)
            addEdge(userId, roleId, 'has_role', graphId);
          });
        }
      });
    });
  });

  // =====================================================
  // Process Alert Edges: User → Alert → Resource hierarchy
  // ONLY include alerts for users that exist in userNodeDetails
  // =====================================================
  Object.entries(alertEdges).forEach(([userEmail, alertMap]) => {
    // CRITICAL: Only process alerts for users that exist in user-nodes API
    if (!userNodeDetails[userEmail]) {
      return;
    }

    // Find the user's graphId (attach alerts to existing user graphs)
    const userGraphId = `user-${userEmail}`;
    const userId = createNodeId('user', userEmail);

    // Ensure user node exists (might not if userEdges didn't have this user)
    if (!nodeIds.has(userId)) {
      const userDetails = getUserDetails(userEmail);
      const counts = getUserCounts(userEmail);
      const userName = userDetails?.userName || userEmail.split('@')[0];
      addNode(userId, 'user', userName, userGraphId, {
        ...userDetails,
        roleCount: counts.roleCount,
        resourceCount: counts.resourceCount,
        alertCount: counts.alertCount,
        criticalAlertCount: counts.criticalAlertCount,
        mostRecentAlertDate: counts.mostRecentAlertDate,
      });
    }

    // Process alerts for this user (merge alerts that share the same title)
    // NOTE: Direct anomaly children of root nodes should NOT have isAlertHierarchy
    // so they are visible initially. Only deeper nodes in alert path have this flag.
    const userAlertGroups = addMergedAlertNodes(
      userId,
      userGraphId,
      Object.entries(alertMap || {}).map(([alertId, resourceName]) => ({ alertId, resourceName })),
      { parentUser: userEmail }
    );

    // Attach affected resources (alert hierarchy - NOT visible initially) for each merged alert
    userAlertGroups.forEach((group) => {
      group.affectedResources.forEach((resourceName) => {
        const alertResourceId = createNodeId('resource', `${userGraphId}-alert-${group.representativeAlertId}-${resourceName}`);
        const resourceDetails = getResourceDetails(resourceName);

        addNode(alertResourceId, 'resource', resourceName, userGraphId, {
          ...resourceDetails,
          parentAlert: group.representativeAlertId,
          isAlertHierarchy: true, // This IS part of alert hierarchy - requires manual expansion
        });

        // Add edge: Alert → Resource
        addEdge(group.alertNodeId, alertResourceId, 'affects', userGraphId);
      });
    });
  });

  // Calculate stats
  const userCount = nodes.filter(n => n.type === 'user' && n.id.startsWith('user-')).length;
  const saCount = nodes.filter(n => n.type === 'service_account').length;
  let resourceCount = 0;
  Object.values(resourceEdges).forEach(resourceMap => {
    resourceCount += Object.keys(resourceMap || {}).length;
  });

  // Debug: confirm root resources were created
  if (cloudType === 'AWS') {
    const rootResourceCount = nodes.filter(n => n.type === 'resource' && n.id.startsWith('resource-center-')).length;
    console.log('[AWS graph] root resource nodes created:', rootResourceCount);
  }

  return {
    nodes,
    edges,
    stats: {
      users: userCount,
      service_accounts: saCount,
      resources: resourceCount,
      edges: edges.length,
    },
  };
};
